import {
  MeetingDto,
  MeetingNoRecordingReason,
  MeetingStatus,
} from '@interfaces/meetings/responses/meeting.response';

/**
 * Regras puras do estado da tela de detalhe (redesenho da opção 1 com o estado "processando"):
 * o que dizer quando não há vídeo e em que etapa está o processamento da gravação.
 */

/** Mensagem do lugar do vídeo quando a reunião não tem gravação. */
export interface ExplicacaoSemVideo {
  titulo: string;
  texto: string;
  /** Ícone `pi` do bloco. */
  icone: string;
}

const MOTIVOS: Record<MeetingNoRecordingReason, ExplicacaoSemVideo> = {
  NobodyJoined: {
    titulo: 'Ninguém entrou pelo Teams',
    texto:
      'O bot entrou e esperou, mas nenhum participante apareceu na call. Acontece em reunião presencial ou em evento que só tinha o link do Teams porque o Outlook coloca por padrão.',
    icone: 'pi pi-users',
  },
  NotAdmitted: {
    titulo: 'O bot não foi admitido na reunião',
    texto:
      'Ele ficou na sala de espera e ninguém o deixou entrar, ou foi recusado. Na próxima, admita o "Cog AI bot" quando ele pedir para entrar.',
    icone: 'pi pi-lock',
  },
  RemovedFromCall: {
    titulo: 'O bot foi removido da call',
    texto: 'Alguém tirou o bot da reunião antes de a gravação começar, por isso não há vídeo.',
    icone: 'pi pi-sign-out',
  },
  RecordingDenied: {
    titulo: 'A gravação não foi permitida',
    texto: 'A plataforma da reunião recusou a permissão para o bot gravar.',
    icone: 'pi pi-ban',
  },
  MeetingUnavailable: {
    titulo: 'O bot não conseguiu abrir a reunião',
    texto:
      'O link não levou à call: a reunião não foi encontrada, o link expirou ou o Teams da organização não deixa convidados de fora entrarem.',
    icone: 'pi pi-link',
  },
  Other: {
    titulo: 'O bot não conseguiu gravar',
    texto: 'A Recall.ai informou um erro do bot nesta reunião.',
    icone: 'pi pi-exclamation-circle',
  },
  BotNotFound: {
    titulo: 'A gravação não está disponível',
    texto:
      'O bot desta reunião não existe mais na Recall.ai, então não há vídeo nem transcrição para recuperar.',
    icone: 'pi pi-question-circle',
  },
};

/**
 * Aviso embaixo do vídeo quando a gravação existe mas não cobre a reunião inteira: hoje, quando o bot
 * foi removido da call depois de começar a gravar (`partialRecordingReason`). Nulo nas demais.
 */
export function avisoDeGravacaoParcial(
  meeting: Pick<MeetingDto, 'status' | 'partialRecordingReason'>
): string | null {
  if (meeting.status !== 'Completed') return null;
  return meeting.partialRecordingReason === 'RemovedFromCall'
    ? 'Gravação parcial: o bot foi removido da call antes de a reunião terminar, então o vídeo e a transcrição param nesse ponto.'
    : null;
}

/**
 * O que mostrar no lugar do vídeo de uma reunião que terminou sem gravação (`NoRecording`/`Failed`),
 * pelo motivo que o backend traduziu do `sub_code` da Recall. Sem motivo (ligações de antes de ele ser
 * guardado), a mensagem genérica de sempre. Nulo nas demais situações.
 */
export function explicacaoSemGravacao(
  meeting: Pick<MeetingDto, 'status' | 'noRecordingReason'>
): ExplicacaoSemVideo | null {
  if (meeting.status !== 'NoRecording' && meeting.status !== 'Failed') return null;

  const motivo = meeting.noRecordingReason ? MOTIVOS[meeting.noRecordingReason] : undefined;
  if (motivo) return motivo;

  return meeting.status === 'Failed'
    ? {
        titulo: 'A gravação falhou',
        texto: 'O bot teve um erro e a gravação não foi concluída.',
        icone: 'pi pi-exclamation-circle',
      }
    : {
        titulo: 'Não houve gravação desta reunião',
        texto: 'O bot não chegou a gravar: não foi admitido, saiu ou ninguém entrou na call.',
        icone: 'pi pi-video',
      };
}

export type EstadoDaEtapa = 'feito' | 'atual' | 'pendente';

export interface EtapaDoProcessamento {
  rotulo: string;
  estado: EstadoDaEtapa;
}

/** O bloco "processando": título, texto e as etapas. */
export interface Processamento {
  titulo: string;
  texto: string;
  etapas: EtapaDoProcessamento[];
}

/** Ordem das situações do bot até a gravação ficar pronta. */
const DEGRAU: Partial<Record<MeetingStatus, number>> = {
  Joining: 0,
  InWaitingRoom: 0,
  Recording: 1,
  Processing: 2,
  Completed: 3,
};

function estadoPelo(degrau: number, etapa: number): EstadoDaEtapa {
  if (degrau > etapa) return 'feito';
  return degrau === etapa ? 'atual' : 'pendente';
}

/**
 * Em que pé está a gravação, enquanto ela ainda não está toda pronta: bot entrando ou na sala de espera,
 * gravando, vídeo sendo processado, transcrição a caminho. O resumo do Cog (PBI 3225) é a última etapa:
 * ele só começa depois da transcrição, então aqui está sempre pendente. Nulo quando não há processamento a mostrar (reunião futura, cancelada, sem gravação ou já com
 * vídeo e transcrição).
 */
export function processamentoDaGravacao(
  meeting: Pick<MeetingDto, 'status' | 'hasRecording' | 'hasTranscript'>
): Processamento | null {
  const degrauBase = DEGRAU[meeting.status];
  if (degrauBase === undefined) return null;
  if (meeting.status === 'Completed' && (!meeting.hasRecording || meeting.hasTranscript))
    return null;

  // Vídeo guardado vale como etapa concluída mesmo que a situação ainda não tenha virado Completed.
  const degrau = meeting.hasRecording ? Math.max(degrauBase, 3) : degrauBase;

  const etapas: EtapaDoProcessamento[] = [
    {
      rotulo: meeting.status === 'InWaitingRoom' ? 'Na sala de espera' : 'Bot na reunião',
      estado: estadoPelo(degrau, 0),
    },
    { rotulo: 'Gravação', estado: estadoPelo(degrau, 1) },
    { rotulo: 'Vídeo processado', estado: estadoPelo(degrau, 2) },
    {
      rotulo: 'Transcrição',
      estado: meeting.hasTranscript ? 'feito' : degrau >= 3 ? 'atual' : 'pendente',
    },
    { rotulo: 'Resumo do Cog', estado: 'pendente' },
  ];

  if (meeting.status === 'InWaitingRoom') {
    return {
      titulo: 'O bot está na sala de espera',
      texto: 'Admita o "Cog AI bot" no Teams para ele começar a gravar.',
      etapas,
    };
  }
  if (meeting.status === 'Joining') {
    return {
      titulo: 'O bot está entrando na reunião',
      texto: 'A gravação começa assim que ele entrar.',
      etapas,
    };
  }
  if (meeting.status === 'Recording') {
    return {
      titulo: 'A reunião está sendo gravada',
      texto: 'O vídeo e a transcrição ficam prontos alguns minutos depois que a call terminar.',
      etapas,
    };
  }
  if (!meeting.hasRecording) {
    return {
      titulo: 'Estamos preparando a gravação',
      texto: 'O vídeo e a transcrição ficam prontos em alguns minutos. Esta tela atualiza sozinha.',
      etapas,
    };
  }
  return {
    titulo: 'Vídeo pronto, transcrição a caminho',
    texto: 'A transcrição é feita depois do vídeo e aparece aqui em alguns minutos.',
    etapas,
  };
}
